<template>
  <div>
    <div class="p-4 pb-0 card-header">
      <h6 class="mb-1">{{ title }}</h6>
      <p class="text-sm mb-1">QR 코드 촬영으로 제품 이력을 조회한 횟수입니다.</p>
    </div>
    <div class="p-3 card-body">
      <div class="chart">
        <canvas ref="canvas" :id="id" class="chart-canvas" :height="height"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import { markRaw, toRaw } from "vue";

export default {
  name: "GradientLineChart",
  props: {
    id: { type: String, required: true },
    height: { type: String, default: "300" },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    // { labels: string[], datasets: [{ label?: string, data: number[] }] }
    chart: { type: Object, required: true },
  },
  data() {
    return { _chart: null, _retry: null, _alive: true };
  },
  mounted() {
    this.queueRender();
  },
  // keep-alive 사용 시 활성/비활성 대응
  activated() {
    this._alive = true;
    this.queueRender();
  },
  deactivated() {
    this._alive = false;
    this.destroyChart();
  },
  beforeUnmount() {
    this._alive = false;
    this.destroyChart();
  },
  watch: {
    chart: {
      deep: true,
      immediate: true,
      handler() {
        // 차트가 있으면 무애니메이션 업데이트, 없으면 안전 렌더 큐
        if (this._chart) this.updateChart();
        else this.queueRender();
      },
    },
  },
  methods: {
    queueRender() {
      // DOM + 레이아웃 준비될 때까지 재시도
      this.$nextTick(() => {
        if (!this._alive) return;
        const canvas = this.$refs.canvas;
        const w = canvas?.parentElement?.clientWidth || 0;
        if (!canvas || !canvas.isConnected || w === 0) {
          clearTimeout(this._retry);
          this._retry = setTimeout(() => this.queueRender(), 50);
          return;
        }
        this.renderChart();
      });
    },
    destroyChart() {
      clearTimeout(this._retry);
      if (this._chart) {
        try { this._chart.stop?.(); } catch {}
        try { this._chart.destroy(); } catch {}
        this._chart = null;
      }
    },
    renderChart() {
      const canvas = this.$refs.canvas;
      if (!canvas || !canvas.isConnected) return;

      this.destroyChart(); // 기존 인스턴스 완전 정리

      const ctx = canvas.getContext("2d");
      if (!ctx) return; // 방어

      const labels = Array.isArray(this.chart?.labels) ? [...this.chart.labels] : [];
      const src = Array.isArray(this.chart?.datasets) ? this.chart.datasets : [];

      const gradient = ctx.createLinearGradient(0, 230, 0, 50);
      gradient.addColorStop(1, "rgba(203,12,159,0.2)");
      gradient.addColorStop(0.2, "rgba(72,72,176,0.0)");
      gradient.addColorStop(0, "rgba(203,12,159,0)");
      const strokes = ["#cb0c9f", "#3A416F", "#17c1e8", "#82d616", "#f53939"];

      const datasets = (src.length ? src : [{ data: [] }]).map((ds, i) => ({
        label: ds.label ?? `Series ${i + 1}`,
        data: Array.isArray(ds.data) ? [...ds.data] : [],
        tension: 0.4,
        pointRadius: 0,
        borderColor: strokes[i % strokes.length],
        borderWidth: 3,
        backgroundColor: gradient,
        fill: true,
      }));

      this._chart = markRaw(new Chart(ctx, {
        type: "line",
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 600, easing: 'easeOutQuart' },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.parsed.y ?? 0}`,
                title: (items) => items?.[0]?.label ?? "",
              }
            }
          },
          interaction: { intersect: false, mode: "index" },
          scales: {
            y: {
              grid: { drawBorder: false, display: true, drawOnChartArea: true, drawTicks: false, borderDash: [5,5] },
              ticks: {
                display: true, padding: 10, color: "#b2b9bf",
                font: { size: 11, family: "Open Sans", style: "normal", lineHeight: 2 },
                precision: 0
              },
            },
            x: {
              grid: { drawBorder: false, display: false, drawOnChartArea: false, drawTicks: false, borderDash: [5,5] },
              ticks: {
                display: true, color: "#b2b9bf", padding: 20,
                font: { size: 11, family: "Open Sans", style: "normal", lineHeight: 2 },
              },
            },
          },
        },
      }));
    },
    updateChart() {
      const canvas = this.$refs.canvas;
      if (!this._chart || !canvas || !canvas.isConnected) {
        // 캔버스가 분리되어 있으면 안전하게 파기 후 재렌더 큐
        this.destroyChart();
        this.queueRender();
        return;
      }
      const ch = toRaw(this._chart);
      ch.data.labels = Array.isArray(this.chart?.labels) ? [...this.chart.labels] : [];
      const src = Array.isArray(this.chart?.datasets) ? this.chart.datasets : [];
      const ctx = canvas.getContext("2d");
      const gradient = ctx?.createLinearGradient(0, 230, 0, 50);
      if (gradient) {
        gradient.addColorStop(1, "rgba(203,12,159,0.2)");
        gradient.addColorStop(0.2, "rgba(72,72,176,0.0)");
        gradient.addColorStop(0, "rgba(203,12,159,0)");
      }
      const strokes = ["#cb0c9f", "#3A416F", "#17c1e8", "#82d616", "#f53939"];
      ch.data.datasets = (src.length ? src : [{ data: [] }]).map((ds, i) => ({
        label: ds.label ?? `Series ${i + 1}`,
        data: Array.isArray(ds.data) ? [...ds.data] : [],
        tension: 0.4,
        pointRadius: 0,
        borderColor: strokes[i % strokes.length],
        borderWidth: 3,
        backgroundColor: gradient ?? ch.data.datasets?.[0]?.backgroundColor ?? "rgba(0,0,0,0.1)",
        fill: true,
      }));
      ch.update();
    },
  },
};
</script>
